import type { Money } from '@/shared/api/types';
import { moneyInputError, moneyToInput, parseMoney } from '@/shared/lib/format';

import { exposicaoInicial, montarExposicao, type EstadoDaExposicao } from './exposicao';
import type {
  BureauSituation,
  DatumOrigin,
  ElementSpec,
  SimulationDatum,
  SimulationRequest,
  VigidataProduct,
} from './types';

/**
 * Estado do formulario do simulador e a montagem do pedido.
 *
 * Tudo aqui e funcao pura, testada sem tela. Dinheiro sai de `parseMoney`, a mesma
 * conversao do resto da aplicacao: a conta e sobre os digitos, sem ponto flutuante,
 * e casa decimal alem do centavo e recusada, e nao arredondada.
 */

/** Elementos que o cadastro do tomador tem e que o motor le dele. */
export const ELEMENTOS_DO_CADASTRO = [
  'razao_social',
  'data_abertura',
  'natureza_juridica',
  'cnae_principal',
  'capital_social',
  'enquadramento',
];

/** CNPJ ficticio de exemplo, valido pelo digito. */
export const DOCUMENTO_FICTICIO = '11.222.333/0001-81';

export interface LinhaDeDado {
  /** Vazio: o dado nao e informado. */
  origem: DatumOrigin | '';
  valor: string;
  dataReferencia: string;
  situacao: BureauSituation | '';
  modelo: string;
}

export type TipoDaAlteracao = 'dinheiro' | 'booleano' | 'inteiro' | 'json';

export interface Alteracao {
  chave: string;
  tipo: TipoDaAlteracao;
  texto: string;
}

export interface EstadoDoSimulador {
  versaoId: string;
  comoHomologada: boolean;
  alteracoes: Alteracao[];
  documento: string;
  dados: Record<string, LinhaDeDado>;
  bureauLigado: boolean;
  produto: VigidataProduct;
  respostaTexto: string;
  dataConsulta: string;
  provedorFicticio: boolean;
  modalidadeId: string;
  referencia: string;
  importanciaSegurada: string;
  /** Exposicao ficticia do tomador (ADR 0088). Ver exposicao.ts. */
  exposicao: EstadoDaExposicao;
}

export function linhaVazia(): LinhaDeDado {
  return { origem: '', valor: '', dataReferencia: '', situacao: '', modelo: '' };
}

export function estadoInicial(): EstadoDoSimulador {
  return {
    versaoId: '',
    comoHomologada: false,
    alteracoes: [],
    documento: DOCUMENTO_FICTICIO,
    dados: {},
    bureauLigado: false,
    produto: 'analise_credito_cnpj',
    respostaTexto: '',
    dataConsulta: '',
    provedorFicticio: false,
    modalidadeId: '',
    referencia: '',
    importanciaSegurada: '',
    exposicao: exposicaoInicial(),
  };
}

/** Origens que o elemento admite, pelos caminhos do retrato real. */
export function origensPermitidas(spec: ElementSpec): DatumOrigin[] {
  const out: DatumOrigin[] = [];
  if (ELEMENTOS_DO_CADASTRO.includes(spec.key)) out.push('cadastro');
  if (spec.recordable) out.push('digitado', 'documento');
  out.push('consulta');
  return out;
}

const DATA = /^\d{4}-\d{2}-\d{2}$/;
const INTEIRO = /^-?\d+$/;

/** Mensagem do valor do dado, ou undefined quando ele pode seguir para o servidor. */
export function erroDoValor(spec: ElementSpec, linha: LinhaDeDado): string | undefined {
  if (linha.origem === '' || linha.valor.trim() === '') return undefined;
  switch (spec.kind) {
    case 'dinheiro':
      return moneyInputError(linha.valor);
    case 'data':
      return DATA.test(linha.valor.trim())
        ? undefined
        : 'Use a data no formato AAAA-MM-DD.';
    case 'inteiro':
      return INTEIRO.test(linha.valor.trim()) &&
        Number.isSafeInteger(Number(linha.valor))
        ? undefined
        : 'Use um numero inteiro.';
    case 'indicador':
      return linha.valor === 'sim' || linha.valor === 'nao'
        ? undefined
        : 'Escolha sim ou nao.';
    default:
      return undefined;
  }
}

/** O dado ficticio no formato do pedido, ou null quando ele nao e informado. */
export function montarDado(
  spec: ElementSpec,
  linha: LinhaDeDado,
): SimulationDatum | null {
  if (linha.origem === '') return null;
  const dado: SimulationDatum = { element: spec.key, origin: linha.origem };
  const valor = linha.valor.trim();
  if (valor !== '') {
    switch (spec.kind) {
      case 'dinheiro': {
        const money = parseMoney(valor);
        if (money) dado.value_money = money;
        break;
      }
      case 'data':
        dado.value_date = valor;
        break;
      case 'indicador':
        dado.value_flag = valor === 'sim';
        break;
      case 'inteiro':
        dado.value_integer = Number(valor);
        break;
      default:
        dado.value_text = valor;
    }
  }
  if (
    (linha.origem === 'documento' || linha.origem === 'consulta') &&
    linha.dataReferencia.trim() !== ''
  ) {
    dado.reference_date = linha.dataReferencia.trim();
  }
  if (linha.origem === 'consulta') {
    if (linha.situacao !== '') dado.situation = linha.situacao;
    if (linha.modelo.trim() !== '') dado.model = linha.modelo.trim();
  }
  return dado;
}

function eMoney(valor: unknown): valor is Money {
  return (
    typeof valor === 'object' &&
    valor !== null &&
    'amount' in valor &&
    'currency' in valor &&
    'exponent' in valor
  );
}

/** Tipo do editor da alteracao, pelo valor atual do parametro. */
export function tipoDaAlteracao(valorAtual: unknown): TipoDaAlteracao {
  if (eMoney(valorAtual)) return 'dinheiro';
  if (typeof valorAtual === 'boolean') return 'booleano';
  if (typeof valorAtual === 'number' && Number.isSafeInteger(valorAtual))
    return 'inteiro';
  return 'json';
}

/** Texto inicial do editor, a partir do valor atual. */
export function textoDaAlteracao(valorAtual: unknown): string {
  switch (tipoDaAlteracao(valorAtual)) {
    case 'dinheiro':
      return moneyToInput(valorAtual as Money);
    case 'booleano':
      return valorAtual ? 'sim' : 'nao';
    case 'inteiro':
      return String(valorAtual);
    default:
      return JSON.stringify(valorAtual ?? null, null, 2);
  }
}

/**
 * Confere que todo numero do valor e inteiro exato. Os parametros do motor nao tem
 * numero com casa decimal (dinheiro em centavos, fator em milesimos), e um inteiro
 * acima do exato do navegador mudaria de valor no caminho.
 */
function soInteirosExatos(valor: unknown): boolean {
  if (typeof valor === 'number') return Number.isSafeInteger(valor);
  if (Array.isArray(valor)) return valor.every(soInteirosExatos);
  if (typeof valor === 'object' && valor !== null) {
    return Object.values(valor).every(soInteirosExatos);
  }
  return true;
}

/** O valor da alteracao para o pedido, ou a mensagem de erro. */
export function valorDaAlteracao(
  alteracao: Alteracao,
): { valor: unknown } | { erro: string } {
  const texto = alteracao.texto.trim();
  switch (alteracao.tipo) {
    case 'dinheiro': {
      if (texto === '') return { valor: null };
      const money = parseMoney(texto);
      return money
        ? { valor: money }
        : { erro: moneyInputError(texto) ?? 'Valor invalido.' };
    }
    case 'booleano':
      return texto === 'sim' || texto === 'nao'
        ? { valor: texto === 'sim' }
        : { erro: 'Escolha sim ou nao.' };
    case 'inteiro':
      return INTEIRO.test(texto) && Number.isSafeInteger(Number(texto))
        ? { valor: Number(texto) }
        : { erro: 'Use um numero inteiro.' };
    default: {
      try {
        const valor: unknown = JSON.parse(texto === '' ? 'null' : texto);
        if (!soInteirosExatos(valor)) {
          return {
            erro: 'Numeros sao inteiros: dinheiro em centavos, fator em milesimos.',
          };
        }
        return { valor };
      } catch {
        return { erro: 'JSON invalido.' };
      }
    }
  }
}

export interface PedidoMontado {
  pedido: SimulationRequest | null;
  erros: Record<string, string>;
}

/** Monta o pedido de simulacao, com o erro de cada campo que nao pode seguir. */
export function montarPedido(
  estado: EstadoDoSimulador,
  elementos: ElementSpec[],
): PedidoMontado {
  const erros: Record<string, string> = {};

  const data: SimulationDatum[] = [];
  for (const spec of elementos) {
    const linha = estado.dados[spec.key];
    if (!linha) continue;
    const erro = erroDoValor(spec, linha);
    if (erro) {
      erros[`dado.${spec.key}`] = erro;
      continue;
    }
    const dado = montarDado(spec, linha);
    if (dado) data.push(dado);
  }

  const parameters: Record<string, unknown> = {};
  for (const alteracao of estado.alteracoes) {
    if (alteracao.chave === '') continue;
    const resultado = valorDaAlteracao(alteracao);
    if ('erro' in resultado) {
      erros[`alteracao.${alteracao.chave}`] = resultado.erro;
    } else {
      parameters[alteracao.chave] = resultado.valor;
    }
  }

  const importancia = parseMoney(estado.importanciaSegurada);
  if (!importancia || importancia.amount <= 0) {
    erros.importanciaSegurada =
      moneyInputError(estado.importanciaSegurada) ??
      'Informe a importancia segurada, maior que zero.';
  }
  if (estado.modalidadeId === '' && estado.referencia === '') {
    erros.modalidade = 'Escolha a modalidade da empresa ou a referencia de modalidade.';
  }

  let bureau: SimulationRequest['bureau'];
  if (estado.bureauLigado) {
    const texto = estado.respostaTexto.trim();
    if (texto !== '') {
      try {
        JSON.parse(texto);
      } catch {
        erros.resposta = 'A resposta colada nao e JSON valido.';
      }
    }
    if (estado.dataConsulta !== '' && !DATA.test(estado.dataConsulta)) {
      erros.dataConsulta = 'Use a data no formato AAAA-MM-DD.';
    }
    bureau = { simulated: estado.provedorFicticio };
    // O texto vai como foi colado: o numero decimal do fornecedor nao passa pelo
    // ponto flutuante do navegador.
    if (texto !== '') {
      bureau.response = texto;
      bureau.product = estado.produto;
    }
    if (estado.dataConsulta !== '') bureau.consulted_at = estado.dataConsulta;
  }

  const exposicao = montarExposicao(estado.exposicao);
  Object.assign(erros, exposicao.erros);

  if (Object.keys(erros).length > 0 || !importancia) {
    return { pedido: null, erros };
  }

  const pedido: SimulationRequest = {
    simulate_as_homologated: estado.comoHomologada,
    policyholder: { document: estado.documento, data },
    operation: { insured_amount: importancia },
  };
  if (estado.versaoId !== '') pedido.parameter_set_id = estado.versaoId;
  if (Object.keys(parameters).length > 0) pedido.parameters = parameters;
  if (bureau) pedido.bureau = bureau;
  if (exposicao.exposicao) pedido.exposure = exposicao.exposicao;
  if (estado.modalidadeId !== '') {
    pedido.operation.modality_id = estado.modalidadeId;
  } else {
    pedido.operation.reference = estado.referencia;
  }
  return { pedido, erros };
}
